import type { AiLodgementRunStatus } from '@shared/prisma/enums';

/**
 * AI claim lodgement from documents (docs/plan/ai-claim-lodgement-from-documents/plan.md): staff
 * drop files and emails, the server unpacks and reads them, and AI prepares a staff lodgement draft.
 */

/** PostHog flag, off by default and stacked on `PRODUCTS_V2_FLAG_KEY`; every surface answers NOT_FOUND while off. */
export const AI_CLAIM_LODGEMENT_FLAG_KEY = 'enable-ai-claim-lodgement';

export const AI_LODGEMENT_LIMITS = {
    /** Files a user can drop into one run. */
    maxRootFiles: 20,
    /** Per dropped file and per unpacked attachment. */
    maxFileBytes: 25 * 1024 * 1024,
    /**
     * Per email, dropped or attached: the server parses an email in one go and refuses a larger one
     * (`EMAIL_EXTRACTION.MAX_RAW_SYNC_EMAIL_BYTES`), so a larger email's attachments are added on
     * their own instead.
     */
    maxEmailBytes: 20 * 1024 * 1024,
    /** All dropped files of one run together. */
    maxRunBytes: 100 * 1024 * 1024,
    /** Dropped files plus unpacked email attachments. */
    maxTotalFiles: 50,
    /**
     * The deepest file unpacked (CUR-1753): a dropped file is depth 0 and an email's attachments sit
     * one deeper. An email at this depth is read for its body; its attachments are listed as too
     * deep, not unpacked.
     */
    maxEmailDepth: 3,
    /**
     * Bytes of attachments one run stores from its emails, at every depth. Each level is inside the
     * one above, so the same bytes can be stored once per level; past this the rest are skipped as
     * too large.
     */
    maxUnpackedBytes: 100 * 1024 * 1024,
    /** Characters of extracted text kept per file. */
    maxExtractedChars: 200_000,
    /** Unlodged runs expire after this many days, matching lodgement drafts. */
    runRetentionDays: 14,
    /**
     * A run held for its person warns them this many days before it expires: in Recent runs, and once
     * in the bell (docs/plan/unattended-ai-lodgement/plan.md § 11).
     */
    heldRunExpiryWarningDays: 3,
    /** A run's extracted text, cards, summary and quotes are removed this many days after it was created. */
    textRetentionDays: 30,
    /** Characters of the note an administrator can write per product for the product selection. */
    selectionHintMaxChars: 500,
} as const;

/**
 * The run statuses the analysis is still at work in: queued, reading the files and choosing the
 * product, or filling in the form. The page waits on such a run, and its status stream (CUR-1604)
 * keeps reading it.
 */
const IN_FLIGHT_AI_LODGEMENT_RUN_STATUSES: ReadonlySet<AiLodgementRunStatus> = new Set([
    'QUEUED',
    'ANALYSING',
    'PREFILLING',
] as const satisfies readonly AiLodgementRunStatus[]);

/** Whether a run's analysis is still at work, so its status can still change without its person. */
export function isAiLodgementRunInFlight(status: AiLodgementRunStatus): boolean {
    return IN_FLIGHT_AI_LODGEMENT_RUN_STATUSES.has(status);
}

/**
 * What a run accepts, by file extension, with the MIME type the server reads it as. Images that
 * no extractor reads (HEIC) are refused until conversion lands (CUR-1756).
 */
export const AI_LODGEMENT_FILE_TYPES = {
    '.pdf': 'application/pdf',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.eml': 'message/rfc822',
    '.msg': 'application/vnd.ms-outlook',
    '.txt': 'text/plain',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
} as const satisfies Record<`.${string}`, string>;

export type AiLodgementFileExtension = keyof typeof AI_LODGEMENT_FILE_TYPES;

export const AI_LODGEMENT_FILE_EXTENSIONS = Object.keys(AI_LODGEMENT_FILE_TYPES) as AiLodgementFileExtension[];

/** The accepted extension of a file name (case-insensitive), or null. */
export function aiLodgementFileExtension(name: string): AiLodgementFileExtension | null {
    const dot = name.lastIndexOf('.');
    if (dot < 0) return null;
    const extension = name.slice(dot).toLowerCase();
    return extension in AI_LODGEMENT_FILE_TYPES ? (extension as AiLodgementFileExtension) : null;
}

/** The MIME type the server reads an accepted file as, or null for an unaccepted name. */
export function aiLodgementMimeType(name: string): string | null {
    const extension = aiLodgementFileExtension(name);
    return extension ? AI_LODGEMENT_FILE_TYPES[extension] : null;
}

export function isAiLodgementEmailExtension(extension: AiLodgementFileExtension): boolean {
    return extension === '.eml' || extension === '.msg';
}
